import React, { useState } from "react";
import { CampaignData } from "./CampaignBuilder";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Users, Info } from "lucide-react";
import { MockBadge } from "@/components/ui/mock-badge";

interface CampaignAudienceSelectorProps {
  campaignData: CampaignData;
  updateCampaignData: (data: Partial<CampaignData>) => void;
}

// Mock data - in real implementation this would come from API
const audienceSegments = [
  { id: "all", name: "All Subscribers", count: 1254 },
  { id: "active", name: "Active Subscribers", count: 987 },
  { id: "new", name: "New Subscribers (Last 30 Days)", count: 168 },
  { id: "engaged", name: "Highly Engaged", count: 542 },
  { id: "dormant", name: "Dormant (No Activity 90+ Days)", count: 210 },
];

export const CampaignAudienceSelector: React.FC<CampaignAudienceSelectorProps> = ({
  campaignData,
  updateCampaignData,
}) => {
  const [selectionType, setSelectionType] = useState<"segments" | "individuals">("segments");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Helper to calculate total audience count
  const calculateAudienceCount = () => {
    if (selectionType === "individuals" && campaignData.selectedSubscriberIds) {
      return campaignData.selectedSubscriberIds.length;
    }

    let totalCount = 0;
    for (const segment of audienceSegments) {
      if (campaignData.audienceSegments.includes(segment.id)) {
        totalCount += segment.count;
      }
    }

    // Subtract excluded segments
    for (const segment of audienceSegments) {
      if (campaignData.excludedSegments.includes(segment.id)) {
        totalCount -= segment.count;
      }
    }

    return Math.max(0, totalCount);
  };

  const handleSegmentToggle = (segmentId: string) => {
    const isSelected = campaignData.audienceSegments.includes(segmentId);
    let updatedSegments = [...campaignData.audienceSegments];
    
    if (isSelected) {
      updatedSegments = updatedSegments.filter(id => id !== segmentId);
    } else {
      updatedSegments.push(segmentId);
      // Remove from excluded if it's there
      const updatedExcluded = campaignData.excludedSegments.filter(id => id !== segmentId);
      updateCampaignData({ excludedSegments: updatedExcluded });
    }
    
    updateCampaignData({ audienceSegments: updatedSegments });
  };

  const handleExclusionToggle = (segmentId: string) => {
    const isExcluded = campaignData.excludedSegments.includes(segmentId);
    let updatedExcluded = [...campaignData.excludedSegments];
    
    if (isExcluded) {
      updatedExcluded = updatedExcluded.filter(id => id !== segmentId);
    } else {
      updatedExcluded.push(segmentId);
      // Remove from selected if it's there
      const updatedSegments = campaignData.audienceSegments.filter(id => id !== segmentId);
      updateCampaignData({ audienceSegments: updatedSegments });
    }
    
    updateCampaignData({ excludedSegments: updatedExcluded });
  };

  return (
    <div className="space-y-6">
      {/* Development Mode Banner */}
      <Card className="border-amber-300 bg-amber-50">
        <CardContent className="p-4 flex items-center gap-3">
          <Info className="h-5 w-5 text-amber-600" />
          <div>
            <h4 className="text-sm font-medium text-amber-800">Mock Audience Data</h4>
            <p className="text-xs text-amber-700 mt-1">
              This audience selector uses simulated data. Segment counts are placeholders and don't represent actual subscribers.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-slate-200">
        <CardHeader className="pb-3 flex flex-row justify-between items-center">
          <CardTitle className="text-base">Selection Type</CardTitle>
          <MockBadge tooltip="Audience selection is simulated with fixed mock data" />
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={selectionType}
            onValueChange={(value) => setSelectionType(value as "segments" | "individuals")}
            className="space-y-4"
          >
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="segments" id="segments" />
              <div className="space-y-1">
                <Label htmlFor="segments" className="font-medium">Segment-based selection</Label>
                <p className="text-sm text-slate-500">Select groups of subscribers based on defined segments</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="individuals" id="individuals" />
              <div className="space-y-1">
                <Label htmlFor="individuals" className="font-medium">Individual selection</Label>
                <p className="text-sm text-slate-500">Select specific subscribers individually</p>
                <div className="flex mt-2 items-center">
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                    Coming in Phase 4
                  </Badge>
                </div>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {selectionType === "segments" && (
        <>
          <Card className="border border-slate-200">
            <CardHeader className="pb-3 flex flex-row justify-between items-center">
              <CardTitle className="text-base">Include Segments</CardTitle>
              <div className="flex items-center gap-2">
                <MockBadge tooltip="These segments contain mock data and do not represent real subscribers" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {audienceSegments.map((segment) => (
                <div key={segment.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center space-x-3">
                    <Checkbox 
                      id={`include-${segment.id}`} 
                      checked={campaignData.audienceSegments.includes(segment.id)}
                      onCheckedChange={() => handleSegmentToggle(segment.id)}
                      disabled={campaignData.excludedSegments.includes(segment.id)}
                    />
                    <Label 
                      htmlFor={`include-${segment.id}`}
                      className="font-normal cursor-pointer"
                    >
                      {segment.name}
                    </Label>
                  </div>
                  <Badge variant="outline" className="text-slate-600">
                    {segment.count.toLocaleString()}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Exclude Segments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {audienceSegments.map((segment) => (
                <div key={segment.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center space-x-3">
                    <Checkbox 
                      id={`exclude-${segment.id}`} 
                      checked={campaignData.excludedSegments.includes(segment.id)}
                      onCheckedChange={() => handleExclusionToggle(segment.id)}
                      disabled={campaignData.audienceSegments.includes(segment.id)}
                    />
                    <Label 
                      htmlFor={`exclude-${segment.id}`}
                      className="font-normal cursor-pointer"
                    >
                      {segment.name}
                    </Label>
                  </div>
                  <Badge variant="outline" className="text-slate-600">
                    {segment.count.toLocaleString()}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {selectionType === "individuals" && (
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Select Individual Subscribers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search subscribers..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="min-h-[200px] flex flex-col items-center justify-center text-slate-500 p-6">
              <Users className="h-12 w-12 mb-3 text-slate-300" />
              <p className="text-center">Individual subscriber selection will be available in Phase 4</p>
              <Button variant="outline" className="mt-4 border-slate-200" disabled>
                Select Subscribers
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-green-100 bg-green-50">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-green-800">Estimated Audience</h4>
            <p className="text-xs text-green-700 mt-1">
              Your campaign will be sent to approximately:
            </p>
          </div>
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 px-3 py-1 text-base">
            {calculateAudienceCount().toLocaleString()} recipients
          </Badge>
        </CardContent>
      </Card>
    </div>
  );
};
